import classNames from 'classnames'
import * as React from 'react'
import { Link, To } from 'react-router-dom'

import { BoxRounding } from '../box'
import { Button, ButtonHTMLProps, ButtonProps, ButtonSizeType } from '../button'
import { DropdownSeparator } from '../dropdown'
import { Icon } from '../icon'
import { SelectItem, SelectOption, SelectWithTooltip } from '../select'
import { LabelWithDelayedSpinner } from './label-with-delayed-spinner'

export type TimeframeBaseOption<ValueType = string> = SelectItem<ValueType> & {
  labelShort: string
  labelPageTitle?: string
  href?: To
}

interface Props<TimeFrame> {
  chartIndex: number
  isCompactView: boolean
  timeFrameGroups: Array<{ label: string; timeframes: TimeFrame[] }>
  selectedTimeframe: string
  onTimeframeSelect: (value: TimeFrame) => void
  favoriteTimeframes: string[]
  onFavoriteTimeframeToggle: (value: TimeFrame) => void
  rounding: keyof typeof BoxRounding
  size?: ButtonSizeType
  isLoading?: boolean
}

const TIMEFRAME_TOOLTIP = 'Interval'

export function TimeframeSelect<TimeFrame extends TimeframeBaseOption>({
  size,
  chartIndex,
  timeFrameGroups,
  selectedTimeframe,
  favoriteTimeframes,
  isLoading = false,
  isCompactView,
  onTimeframeSelect,
  onFavoriteTimeframeToggle,
  rounding,
}: Props<TimeFrame>) {
  const flatTimeframes = React.useMemo(() => timeFrameGroups.flatMap(({ timeframes }) => timeframes), [timeFrameGroups])

  const buttonProps = React.useMemo<ButtonProps & ButtonHTMLProps>(
    () => ({
      size,
      rounding,
      rightContent: undefined,
      className: '',
      theme: 'chip',
      appearance: !isCompactView ? 'square' : undefined,
    }),
    [size, isCompactView, rounding]
  )

  const selectWithTooltipListProps = React.useMemo(() => ({ rounding }), [rounding])

  return (
    <SelectWithTooltip
      {...buttonProps}
      key={chartIndex}
      aria-label={TIMEFRAME_TOOLTIP}
      onChange={onTimeframeSelect}
      value={selectedTimeframe}
      items={flatTimeframes}
      active={isCompactView}
      tooltipLabel={TIMEFRAME_TOOLTIP}
      leftContent={
        <div className="relative h-4 w-4">
          <LabelWithDelayedSpinner label={<Icon name="interval" />} isLoading={isLoading && isCompactView} />
        </div>
      }
      buttonContent={isCompactView ? flatTimeframes.find(({ value }) => value === selectedTimeframe)?.labelShort : ''}
      data-testid={`chart-${chartIndex}-timeframes-select`}
      hasPopoverViewportHeightLimit
      isNativeMobileSelect={false}
      hasPopoverButtonWidthLimit={false}
      listProps={selectWithTooltipListProps}
    >
      {timeFrameGroups.flatMap(({ timeframes }, groupIndex) => [
        <DropdownSeparator key={`separator-${groupIndex}-${favoriteTimeframes.length}`} />,
        ...timeframes.map((item) => {
          const isIconButtonActive = favoriteTimeframes.includes(item.value)
          return (
            <SelectOption
              as={item.href ? Link : undefined}
              to={item.href!}
              key={`${item.value}${favoriteTimeframes.length}`}
              data-testid={`chart-${chartIndex}-timeframe-select-${item.value}`}
              rounding={rounding}
              rightContent={
                <Button
                  as="div"
                  theme="transparent"
                  appearance="square"
                  data-testid={`chart-${chartIndex}-timeframe-select-${item.value}-toggle-favorite`}
                  onMouseDown={(ev: React.MouseEvent<HTMLDivElement>) => {
                    ev.stopPropagation()
                    ev.preventDefault()
                    onFavoriteTimeframeToggle(item)
                  }}
                  className={classNames('group -mr-2', {
                    'text-gray-300': !isIconButtonActive,
                    'text-yellow-200': isIconButtonActive,
                  })}
                >
                  <Icon
                    name="star"
                    className={classNames({
                      'group-hover:hidden': isIconButtonActive,
                      'hidden group-hover:block': !isIconButtonActive,
                    })}
                  />
                  <Icon
                    name="starOutlined"
                    className={classNames({
                      'group-hover:hidden': !isIconButtonActive,
                      'hidden group-hover:block': isIconButtonActive,
                    })}
                  />
                </Button>
              }
            >
              {item.label}
            </SelectOption>
          )
        }),
      ])}
    </SelectWithTooltip>
  )
}
